<script lang="ts" setup>
import AppCard from "@/components/AppCard.vue";
import AppCardContent from "@/components/AppCardContent.vue";
import AppCardFooter from "@/components/AppCardFooter.vue";
import AppCardFooterContent from "@/components/AppCardFooterContent.vue";
import AppCardHeader from "@/components/AppCardHeader.vue";
import AppCardHoverActivator from "@/components/AppCardHoverActivator.vue";
import AppContentDivided from "@/components/AppContentDivided.vue";
import AppTextHeading from "@/components/AppTextHeading.vue";
import AppTextMuted from "@/components/AppTextMuted.vue";
import AppTextTitle from "@/components/AppTextTitle.vue";
import DokkanCharacterList from "@/components/DokkanCharacterList.vue";
import DokkanSupportMemoryList from "@/components/DokkanSupportMemoryList.vue";
import useCharacter from "@/composables/character";
import { Category } from "@/types/category";
import { Character } from "@/types/character";
import { ArrowRight } from "lucide-vue-next";
import { computed } from "vue";
import { route } from "ziggy-js";

interface Props {
  category: Category;
}

const props = defineProps<Props>();

const leaders = computed<Character[]>(() => {
  return props.category.characters.leaders
    .map((character: Character) => {
      let hp = 0;
      let atk = 0;
      let def = 0;
      let otherCategory = null;
      let incomplete = false;

      const effects = useCharacter(character).leaderSkill.value.effects.filter(
        (e: object) => [1, 2, 3, 82, 93, 104].indexOf(e.type) !== -1,
      );

      effects
        .filter((e: object) =>
          e.categories.find((c) => c.id === props.category.id),
        )
        .filter((e: object, i: number, self: object[]) => {
          const values = JSON.stringify(e.values);

          return (
            self.findIndex(
              (compare: object) => JSON.stringify(compare.values) === values,
            ) === i
          );
        })
        .forEach((effect: object) => {
          if (effect.categories.length > 1) {
            otherCategory = effect.categories.find(
              (c: Category) => c.id !== props.category.id,
            );
          }

          switch (effect.type) {
            case 1:
              hp += 0;
              atk += effect.values[0];
              def += 0;
              break;
            case 2:
              hp += 0;
              atk += 0;
              def += effect.values[0];
              break;
            case 3:
              hp += 0;
              atk += effect.values[0];
              def += effect.values[1];
              break;
            case 82:
              hp += effect.values[1];
              atk += effect.values[1];
              def += effect.values[1];
              break;
            case 93:
              hp += effect.values[1];
              atk += 0;
              def += 0;
              break;
            case 104:
              hp += effect.values[0];
              atk += effect.values[1];
              def += effect.values[2];
              break;
            default:
              hp += 0;
              atk += 0;
              def += 0;
          }
        });

      if (otherCategory !== null) {
        incomplete = true;

        const otherEffects = useCharacter(character)
          .leaderSkill.value.effects.filter(
            (e: object) =>
              [1, 2, 3, 82, 93, 104].indexOf(e.type) !== -1 &&
              e.categories.length === 1 &&
              e.categories.find((c) => c.id === otherCategory.id),
          )
          .forEach((effect: object) => {
            switch (effect.type) {
              case 1:
                hp += 0;
                atk += effect.values[0];
                def += 0;
                break;
              case 2:
                hp += 0;
                atk += 0;
                def += effect.values[0];
                break;
              case 3:
                hp += 0;
                atk += effect.values[0];
                def += effect.values[1];
                break;
              case 82:
                hp += effect.values[1];
                atk += effect.values[1];
                def += effect.values[1];
                break;
              case 93:
                hp += effect.values[1];
                atk += 0;
                def += 0;
                break;
              case 104:
                hp += effect.values[0];
                atk += effect.values[1];
                def += effect.values[2];
                break;
              default:
                hp += 0;
                atk += 0;
                def += 0;
            }
          });
      }

      return {
        ...character,
        boost: {
          hp: hp,
          atk: atk,
          def: def,
          incomplete: incomplete,
        },
      };
    })
    .sort((a, b) => {
      const sumA = a.boost.hp + a.boost.atk + a.boost.def;
      const sumB = b.boost.hp + b.boost.atk + b.boost.def;
      return sumB - sumA;
    });
});
</script>

<template>
  <AppCard>
    <AppCardHeader>
      <AppTextTitle prepended>{{ category.name }}</AppTextTitle>
    </AppCardHeader>

    <AppContentDivided>
      <AppCardContent>
        <AppTextHeading>Leaders</AppTextHeading>
        <DokkanCharacterList :characters="leaders">
          <template v-slot="{ character }">
            <div class="flex gap-1 font-condensed text-xs">
              <span class="basis-1/3"
                >HP:
                <AppTextMuted class="font-medium"
                  >{{ character.boost.hp }}%</AppTextMuted
                ></span
              >
              <span class="basis-1/3"
                >ATK:
                <AppTextMuted class="font-medium"
                  >{{ character.boost.atk }}%</AppTextMuted
                ></span
              >
              <span class="basis-1/3"
                >DEF:
                <AppTextMuted class="font-medium"
                  >{{ character.boost.def }}%</AppTextMuted
                ></span
              >
            </div>
          </template>
        </DokkanCharacterList>
      </AppCardContent>

      <AppCardContent v-if="category.characters.support.length">
        <AppTextHeading>Support</AppTextHeading>
        <DokkanCharacterList
          :characters="category.characters.support"
          :scale="0.75"
        />
      </AppCardContent>

      <AppCardContent v-if="category.support_memories.length">
        <div class="flex flex-col gap-2">
          <AppTextHeading>Support Memories</AppTextHeading>
          <DokkanSupportMemoryList
            :support-memories="category.support_memories"
            class="lg:grid-cols-2 xl:grid-cols-3"
            with-descriptions
          />
        </div>
      </AppCardContent>
    </AppContentDivided>

    <AppCardFooter>
      <AppCardHoverActivator
        :href="route('characters.index', { categories: [category.id] })"
      >
        <AppCardFooterContent
          >View all {{ category.name }} characters
          <ArrowRight />
        </AppCardFooterContent>
      </AppCardHoverActivator>
    </AppCardFooter>
  </AppCard>
</template>

<style scoped></style>
